import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UserItem } from '@/types/userItem';

export type BulkListPriceDecision =
  | { kind: 'price'; priceCents: number; acceptsOffers?: boolean }
  | { kind: 'offers' }
  | { kind: 'skip' };

export interface BulkListPriceQueueSummary {
  priced: number;
  offers: number;
  skipped: number;
}

export interface UseBulkListPriceQueueResult {
  index: number;
  total: number;
  current: UserItem | null;
  isDone: boolean;
  decisions: Map<string, BulkListPriceDecision>;
  summary: BulkListPriceQueueSummary;
  price: (priceCents: number, acceptsOffers?: boolean) => void;
  offersOnly: () => void;
  skip: () => void;
  back: () => void;
  reset: () => void;
}

/** Walks through unpriced items one at a time, collecting a decision per item. */
export function useBulkListPriceQueue(
  items: UserItem[],
  active: boolean,
): UseBulkListPriceQueueResult {
  // Snapshot items on activation so mid-queue prop changes don't shuffle order.
  const [queue, setQueue] = useState<UserItem[]>(items);
  const [index, setIndex] = useState(0);
  const [decisions, setDecisions] = useState<Map<string, BulkListPriceDecision>>(new Map());

  useEffect(() => {
    if (active) {
      setQueue(items);
      setIndex(0);
      setDecisions(new Map());
    }
    // Intentionally omit `items` — snapshot only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const total = queue.length;
  const current = index < total ? queue[index] : null;
  const isDone = index >= total;

  const record = useCallback((decision: BulkListPriceDecision) => {
    setDecisions((prev) => {
      const item = queue[index];
      if (!item) return prev;
      const next = new Map(prev);
      next.set(item.id, decision);
      return next;
    });
    setIndex((i) => Math.min(i + 1, total));
  }, [queue, index, total]);

  const price = useCallback(
    (priceCents: number, acceptsOffers?: boolean) =>
      record({ kind: 'price', priceCents, acceptsOffers }),
    [record],
  );
  const offersOnly = useCallback(() => record({ kind: 'offers' }), [record]);
  const skip = useCallback(() => record({ kind: 'skip' }), [record]);

  const back = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
    setDecisions((prev) => {
      const item = queue[Math.max(0, index - 1)];
      if (!item) return prev;
      const next = new Map(prev);
      next.delete(item.id);
      return next;
    });
  }, [queue, index]);

  const reset = useCallback(() => {
    setIndex(0);
    setDecisions(new Map());
  }, []);

  const summary = useMemo<BulkListPriceQueueSummary>(() => {
    let priced = 0;
    let offers = 0;
    let skipped = 0;
    for (const d of decisions.values()) {
      if (d.kind === 'price') priced += 1;
      else if (d.kind === 'offers') offers += 1;
      else skipped += 1;
    }
    return { priced, offers, skipped };
  }, [decisions]);

  return {
    index,
    total,
    current,
    isDone,
    decisions,
    summary,
    price,
    offersOnly,
    skip,
    back,
    reset,
  };
}
